import React, { Suspense, lazy, useState, useEffect, Component } from 'react';

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div style={{padding:32,color:'red',fontFamily:'monospace',fontSize:13}}>
        <strong>Render error:</strong><br/>
        {String(this.state.error?.message || this.state.error)}
      </div>
    );
    return this.props.children;
  }
}
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import ForcePasswordChange from './components/ForcePasswordChange';
import { LanguageProvider } from './context/LanguageContext';
import { getStudents, getWaiting, getRequestsCount } from './api/client';
import { Menu } from 'lucide-react';
import './styles/global.css';

// Lazy load pages
const LoginPage    = lazy(() => import('./pages/LoginPage'));
const Dashboard    = lazy(() => import('./pages/Dashboard'));
const WaitingPage  = lazy(() => import('./pages/WaitingPage'));
const StudentsPage = lazy(() => import('./pages/StudentsPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const FamiliesPage = lazy(() => import('./pages/FamiliesPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const ButPage      = lazy(() => import('./pages/ButPage'));
const ReportsPage  = lazy(() => import('./pages/ReportsPage'));
const CustomizingPage = lazy(() => import('./pages/CustomizingPage'));
const UsersPage    = lazy(() => import('./pages/UsersPage'));
const ImportPage      = lazy(() => import('./pages/ImportPage'));
const AnmeldungPage   = lazy(() => import('./pages/AnmeldungPage'));
const AntraegePage    = lazy(() => import('./pages/AntraegePage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace/>;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace/>;
  return children;
}

function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState({ waiting: 0, students: 0 });
  const [sidebarPinned, setSidebarPinned] = useState(
    () => localStorage.getItem('mk_sidebar_pinned') !== 'false'
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const togglePin = () => setSidebarPinned(p => {
    const next = !p;
    localStorage.setItem('mk_sidebar_pinned', next);
    return next;
  });

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!user || user.mustChangePassword) return;
    Promise.all([getStudents(), getWaiting(), getRequestsCount()]).then(([sr, wr, rr]) => {
      setCounts({
        students: sr.data.filter(s => s.status === 'active').length,
        waiting: wr.data.length,
        antraege: rr.data.count || 0,
      });
    }).catch(() => {});
  }, [user, location.pathname]);

  if (!user) return <Navigate to="/login" replace/>;
  // Forced password change blocks the whole app until the user sets a new one.
  if (user.mustChangePassword) return <ForcePasswordChange/>;

  return (
    <div className="app-shell">
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
          <Menu size={22}/>
        </button>
        <span className="mobile-topbar-title">Maktab</span>
      </div>

      {/* Backdrop for mobile drawer */}
      <div
        className={`sidebar-backdrop${mobileOpen ? ' mobile-open' : ''}`}
        onClick={() => setMobileOpen(false)}
      />

      <Sidebar
        counts={counts}
        pinned={sidebarPinned}
        onTogglePin={togglePin}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <main className={`main${sidebarPinned ? '' : ' main-floating'}`}>
        <ErrorBoundary>
        <Suspense fallback={<div className="page-loading"><div className="spinner"/></div>}>
          <Routes>
            <Route path="/dashboard"    element={<ProtectedRoute><Dashboard/></ProtectedRoute>}/>
            <Route path="/waiting"      element={<ProtectedRoute><WaitingPage/></ProtectedRoute>}/>
            <Route path="/students"     element={<ProtectedRoute><StudentsPage/></ProtectedRoute>}/>
            <Route path="/attendance"   element={<ProtectedRoute roles={['admin','director','teacher','office']}><AttendancePage/></ProtectedRoute>}/>
            <Route path="/families"     element={<ProtectedRoute><FamiliesPage/></ProtectedRoute>}/>
            <Route path="/payments"     element={<ProtectedRoute roles={['admin','director','accountant','parent']}><PaymentsPage/></ProtectedRoute>}/>
            <Route path="/but"          element={<ProtectedRoute roles={['admin','director','accountant']}><ButPage/></ProtectedRoute>}/>
            <Route path="/reports"      element={<ProtectedRoute roles={['admin','director','accountant']}><ReportsPage/></ProtectedRoute>}/>
            <Route path="/customizing"  element={<ProtectedRoute roles={['admin']}><CustomizingPage/></ProtectedRoute>}/>
            <Route path="/users"        element={<ProtectedRoute roles={['admin']}><UsersPage/></ProtectedRoute>}/>
            <Route path="/import"       element={<ProtectedRoute roles={['admin']}><ImportPage/></ProtectedRoute>}/>
            <Route path="/antraege"     element={<ProtectedRoute roles={['admin','director','office']}><AntraegePage/></ProtectedRoute>}/>
            <Route path="*"             element={<Navigate to="/dashboard" replace/>}/>
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
        <Toaster position="top-right" toastOptions={{
          style: { fontFamily:'Inter, system-ui, sans-serif', fontSize:'12.5px', borderRadius:'6px', border:'1px solid var(--border)' }
        }}/>
        <Suspense fallback={<div className="page-loading"><div className="spinner"/></div>}>
          <Routes>
            <Route path="/login"     element={<LoginWithRedirect/>}/>
            <Route path="/reset-password" element={<ResetPasswordPage/>}/>
            <Route path="/anmeldung" element={<AnmeldungPage/>}/>
            <Route path="/*"         element={<AppShell/>}/>
          </Routes>
        </Suspense>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function LoginWithRedirect() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace/>;
  return <LoginPage/>;
}
